import { z } from 'zod';
import {
  MAX_NAME_LENGTH,
  MAX_ROUNDS,
  MIN_NAME_LENGTH,
  MIN_ROUNDS,
  QUESTION_NUMBER_MAX,
  QUESTION_NUMBER_MIN,
  ROOM_CODE_ALPHABET,
  ROOM_CODE_LENGTH,
} from './constants.js';

/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */

/** Server-assigned, stable for the lifetime of the room (survives reconnect). */
export const PlayerIdSchema = z.string().min(1).max(64);
export type PlayerId = z.infer<typeof PlayerIdSchema>;

/** Opaque token bound to `room + seat`, held in localStorage (SPEC §5.4). */
export const SessionTokenSchema = z.string().min(1).max(128);
export type SessionToken = z.infer<typeof SessionTokenSchema>;

/**
 * Opaque token bound to a *room*, minted when the room is created and given to
 * the creating device only (SPEC §5.5). Replayed in `join` to claim host.
 */
export const HostTokenSchema = z.string().min(1).max(128);
export type HostToken = z.infer<typeof HostTokenSchema>;

export const RoomCodeSchema = z
  .string()
  .length(ROOM_CODE_LENGTH)
  .regex(new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`), 'invalid room code');
export type RoomCode = z.infer<typeof RoomCodeSchema>;

export const PlayerNameSchema = z.string().trim().min(MIN_NAME_LENGTH).max(MAX_NAME_LENGTH);
export type PlayerName = z.infer<typeof PlayerNameSchema>;

export const QuestionNumberSchema = z
  .number()
  .int()
  .min(QUESTION_NUMBER_MIN)
  .max(QUESTION_NUMBER_MAX);
export type QuestionNumber = z.infer<typeof QuestionNumberSchema>;

export const RoundNumberSchema = z.number().int().min(1).max(MAX_ROUNDS);
export type RoundNumber = z.infer<typeof RoundNumberSchema>;

/**
 * Photo as a `data:` URL (WebP, ~30–50 KB per SPEC §6). Photos never get a real
 * URL — they travel over the socket and live only in the room's DO storage.
 */
export const PhotoDataUrlSchema = z
  .string()
  .startsWith('data:image/')
  .max(400_000, 'photo too large; resize client-side before upload');
export type PhotoDataUrl = z.infer<typeof PhotoDataUrlSchema>;

export const ThumbsVoteSchema = z.enum(['up', 'down']);
export type ThumbsVote = z.infer<typeof ThumbsVoteSchema>;

/* -------------------------------------------------------------------------- */
/* Room phases — the state machine (SPEC §2.3)                                 */
/* -------------------------------------------------------------------------- */

export const RoomPhaseSchema = z.enum([
  /** Pre-game: joining, profiles, host settings. */
  'lobby',
  /** Turn player picks a number from the numbered grid. */
  'question-select',
  /** Simultaneous, untimed: turn player ranks, everyone else predicts. */
  'rank-and-predict',
  /**
   * 10s guillotine after the turn player submits — a backstop for stragglers,
   * not a fixture. Skipped entirely when every connected predictor has already
   * locked (SPEC §2.3 phase 3).
   */
  'lock-countdown',
  /** Synchronised reveal: one ordered list, filled ends-first, then attributed. */
  'reveal',
  /** The turn player argues out loud; the full ranking is on every phone as
   *  reference material (SPEC §5.2). Thumbs widget lives here. */
  'justification',
  /** Shown only at the end of each round. */
  'round-leaderboard',
  /** Final leaderboard + superlatives. */
  'superlatives',
  /** Terminal. Room is done; "play again" resets to lobby. */
  'ended',
]);
export type RoomPhase = z.infer<typeof RoomPhaseSchema>;

/**
 * Legal phase transitions. The DO is authoritative; this table exists so both
 * sides (and tests) can assert the machine is being driven correctly.
 *
 * `rank-and-predict -> reveal` is the countdown skip (SPEC §2.3 phase 3). When
 * every connected predictor has locked before the ranking lands there is nobody
 * left to wait for, so making the room sit through ten seconds of guillotine
 * animation would be a delay with no purpose behind it. `lock-countdown` is
 * therefore a phase a turn may never visit, and nothing downstream may assume it
 * happened.
 */
export const PHASE_TRANSITIONS: Readonly<Record<RoomPhase, readonly RoomPhase[]>> = Object.freeze({
  lobby: ['question-select', 'ended'],
  'question-select': ['rank-and-predict', 'round-leaderboard', 'superlatives', 'ended'],
  'rank-and-predict': ['lock-countdown', 'reveal', 'question-select', 'superlatives', 'ended'],
  'lock-countdown': ['reveal', 'ended'],
  reveal: ['justification', 'ended'],
  justification: ['question-select', 'round-leaderboard', 'superlatives', 'ended'],
  'round-leaderboard': ['question-select', 'superlatives', 'ended'],
  superlatives: ['lobby', 'ended'],
  ended: ['lobby'],
});

export function canTransition(from: RoomPhase, to: RoomPhase): boolean {
  return PHASE_TRANSITIONS[from].includes(to);
}

/* -------------------------------------------------------------------------- */
/* Player                                                                      */
/* -------------------------------------------------------------------------- */

export const PlayerSchema = z.object({
  id: PlayerIdSchema,
  name: PlayerNameSchema,
  /**
   * Absent until the player has completed the camera/upload step. Broadcast
   * separately from the roster when it changes, because it is large.
   */
  photo: PhotoDataUrlSchema.nullable(),
  connected: z.boolean(),
  isHost: z.boolean(),
  /** Fixed at game start (SPEC §2.1); null while in the lobby. */
  turnOrder: z.number().int().min(0).nullable(),
  /** Explicitly removed by the host (SPEC §5.4). Kept for historical rankings. */
  removed: z.boolean(),
  /** ms epoch; used for host succession (longest-connected player). */
  joinedAt: z.number().int(),
});
export type Player = z.infer<typeof PlayerSchema>;

/** Roster entry without the photo payload, for the frequent broadcasts. */
export const RosterEntrySchema = PlayerSchema.omit({ photo: true }).extend({
  hasPhoto: z.boolean(),
});
export type RosterEntry = z.infer<typeof RosterEntrySchema>;

/* -------------------------------------------------------------------------- */
/* Settings, questions, predictions                                            */
/* -------------------------------------------------------------------------- */

export const RoomSettingsSchema = z.object({
  rounds: z.number().int().min(MIN_ROUNDS).max(MAX_ROUNDS),
  deckId: z.string().min(1),
});
export type RoomSettings = z.infer<typeof RoomSettingsSchema>;

export const QuestionSchema = z.object({
  /** Stable D1 row id — what telemetry is keyed on. */
  id: z.number().int(),
  /** Position in the deck; what the player taps on the grid. */
  number: QuestionNumberSchema,
  deckId: z.string().min(1),
  text: z.string().min(1),
  /** Reserved for future Standard / Filthy / Work-Safe decks (SPEC §3.1). */
  spiceLevel: z.number().int().min(0).max(3),
  /** Reserved; no player-name templating in v1 (SPEC §3.1). */
  template: z.string().nullable(),
});
export type Question = z.infer<typeof QuestionSchema>;

/**
 * A predictor's two picks. Both required — partial picks earn nothing.
 *
 * Note what that rule does *not* say: it penalises an incomplete pair, not an
 * unlocked one. A complete pair on record at the lock scores whether or not its
 * owner ever pressed Submit (SPEC §2.3 phase 3). Locking is a statement about
 * waiting, not about scoring.
 */
export const PredictionSchema = z.object({
  top: PlayerIdSchema,
  bottom: PlayerIdSchema,
});
export type Prediction = z.infer<typeof PredictionSchema>;

/** The turn player's canonical answer: every player, including themselves. */
export const RankingSchema = z.array(PlayerIdSchema).min(1);
export type Ranking = z.infer<typeof RankingSchema>;

/* -------------------------------------------------------------------------- */
/* Turn / round bookkeeping                                                    */
/* -------------------------------------------------------------------------- */

export const TurnStateSchema = z.object({
  /** 0-based index across the whole game. */
  index: z.number().int().min(0),
  round: RoundNumberSchema,
  /** 0-based position within the round. */
  positionInRound: z.number().int().min(0),
  turnPlayerId: PlayerIdSchema,
  question: QuestionSchema.nullable(),
  /** Set once the lock countdown starts (ms epoch, server clock). */
  lockEndsAt: z.number().int().nullable(),
  /** Voided by host skip (SPEC §5.4) — no scoring. */
  voided: z.boolean(),
});
export type TurnState = z.infer<typeof TurnStateSchema>;

/**
 * Completed turn. This is the whole game history the DO keeps, and the input to
 * superlative computation. It never leaves the room's DO in raw form.
 */
export const TurnRecordSchema = z.object({
  index: z.number().int().min(0),
  round: RoundNumberSchema,
  turnPlayerId: PlayerIdSchema,
  questionId: z.number().int(),
  questionNumber: QuestionNumberSchema,
  /**
   * The question as it was shown, denormalised at record time.
   *
   * The record already carries `questionId`, but the post-game ranking history
   * (SPEC §2.6) has to render the question text and the DO has no question
   * store to look it up in — the deck lives in D1 and a room that has since
   * been reshuffled or re-seeded cannot be trusted to return the same row. The
   * text the room actually asked is the only correct thing to replay, so it is
   * kept with the turn rather than re-fetched.
   */
  questionText: z.string().min(1),
  /** The canonical ranking, first place first. */
  ranking: RankingSchema,
  /** Only predictors who submitted both picks before the lock. */
  predictions: z.record(PlayerIdSchema, PredictionSchema),
  /** Points awarded this turn, by player id. */
  points: z.record(PlayerIdSchema, z.number()),
  voided: z.boolean(),
});
export type TurnRecord = z.infer<typeof TurnRecordSchema>;

/**
 * One completed turn as the post-game ranking history renders it (SPEC §2.6).
 *
 * Deliberately *not* `TurnRecord`: the internal record carries predictions and
 * per-player points, which are a reveal-time and scoring concern. The history
 * screen answers one question — "what did each person actually say?" — so it
 * carries only what is needed to draw the question and the order, and nothing
 * that would have to be re-derived on the client.
 *
 * Photos and names are absent on purpose: every client already holds the roster
 * and the photo cache, so re-sending twelve images per turn would multiply the
 * heaviest payload in the app (SPEC §6) for no gain.
 *
 * Voided/skipped turns write no `TurnRecord` and therefore never appear here.
 */
export const RankingHistoryEntrySchema = z.object({
  /** 0-based index across the whole game. Stable key, and the natural sort. */
  turnIndex: z.number().int().min(0),
  /** So the history can be grouped under round headings. */
  round: RoundNumberSchema,
  turnPlayerId: PlayerIdSchema,
  /** The grid position they tapped; part of the ritual, so it is shown. */
  questionNumber: QuestionNumberSchema,
  /** Denormalised at record time — see `TurnRecordSchema.questionText`. */
  questionText: z.string().min(1),
  /** The canonical ranking, first place first. Rendered as one ordered list. */
  ranking: RankingSchema,
});
export type RankingHistoryEntry = z.infer<typeof RankingHistoryEntrySchema>;

/** Every completed turn of the game, oldest first. */
export const RankingHistorySchema = z.array(RankingHistoryEntrySchema);
export type RankingHistory = z.infer<typeof RankingHistorySchema>;

/* -------------------------------------------------------------------------- */
/* Reveal, leaderboard, superlatives                                           */
/* -------------------------------------------------------------------------- */

/** Who predicted this player for this slot, by name (SPEC §2.3 phase 4). */
export const AttributionSchema = z.object({
  playerId: PlayerIdSchema,
  rank: z.number().int().min(0),
  predictedTopBy: z.array(PlayerIdSchema),
  predictedBottomBy: z.array(PlayerIdSchema),
});
export type Attribution = z.infer<typeof AttributionSchema>;

export const ScoreDeltaSchema = z.object({
  playerId: PlayerIdSchema,
  correctTop: z.boolean(),
  correctBottom: z.boolean(),
  perfectRead: z.boolean(),
  /** Zero for the turn player, non-submitters, and voided turns. */
  points: z.number(),
});
export type ScoreDelta = z.infer<typeof ScoreDeltaSchema>;

export const RevealPayloadSchema = z.object({
  turnIndex: z.number().int().min(0),
  round: RoundNumberSchema,
  turnPlayerId: PlayerIdSchema,
  question: QuestionSchema,
  ranking: RankingSchema,
  /** Ordered like `ranking`. */
  attribution: z.array(AttributionSchema),
  /** Every player's delta. Clients display their own; the DO sends all. */
  deltas: z.array(ScoreDeltaSchema),
  /** ms epoch the animation should begin, so all phones start together. */
  startAt: z.number().int(),
});
export type RevealPayload = z.infer<typeof RevealPayloadSchema>;

export const LeaderboardRowSchema = z.object({
  playerId: PlayerIdSchema,
  total: z.number(),
  /** Points earned in the round just finished. */
  roundPoints: z.number(),
  /** 1-based; ties share a rank. */
  rank: z.number().int().min(1),
});
export type LeaderboardRow = z.infer<typeof LeaderboardRowSchema>;

export const LeaderboardSchema = z.object({
  round: RoundNumberSchema,
  final: z.boolean(),
  rows: z.array(LeaderboardRowSchema),
});
export type Leaderboard = z.infer<typeof LeaderboardSchema>;

export const SuperlativeKindSchema = z.enum([
  /** Most-picked-for-bottom across the whole game. */
  'most-picked-for-bottom',
  /** Highest share of correct picks. */
  'best-predictor',
  /** The player nobody could read: predictions about them were most often wrong. */
  'unreadable',
  /** The ranking that most disagreed with the room's predictions. */
  'most-contrarian-ranking',
]);
export type SuperlativeKind = z.infer<typeof SuperlativeKindSchema>;

export const SuperlativeSchema = z.object({
  kind: SuperlativeKindSchema,
  title: z.string(),
  /** Winner(s). Empty when there is not enough data to award it. */
  playerIds: z.array(PlayerIdSchema),
  /** Human-readable stat, e.g. "picked for bottom 7 times". */
  detail: z.string(),
  /** Raw value behind `detail`, for client formatting/animation. */
  value: z.number(),
  /** Set for 'most-contrarian-ranking': which turn it refers to. */
  turnIndex: z.number().int().min(0).nullable(),
});
export type Superlative = z.infer<typeof SuperlativeSchema>;

export const SuperlativesSchema = z.array(SuperlativeSchema);
export type Superlatives = z.infer<typeof SuperlativesSchema>;

/* -------------------------------------------------------------------------- */
/* The snapshot a client renders from                                          */
/* -------------------------------------------------------------------------- */

export const RoomSnapshotSchema = z.object({
  roomCode: RoomCodeSchema,
  phase: RoomPhaseSchema,
  settings: RoomSettingsSchema,
  hostId: PlayerIdSchema.nullable(),
  players: z.array(RosterEntrySchema),
  /** Fixed at game start; empty in the lobby. */
  turnOrder: z.array(PlayerIdSchema),
  /** Null in lobby / after the game ends. */
  turn: TurnStateSchema.nullable(),
  /** Greyed out on the numbered grid; survives "play again" (SPEC §2.6). */
  usedQuestionNumbers: z.array(QuestionNumberSchema),
  /** Running totals. Not rendered mid-round (SPEC §2.4) but always available. */
  totals: z.record(PlayerIdSchema, z.number()),
  /**
   * For the current turn only: how many predictions are in, and expected.
   *
   * Zeroed for the recipient who is the turn player, for the same reason
   * `prediction_count` is withheld from them (SPEC §2.3 phase 3 — the clock is
   * in their hands, so a live tally is only something to stare at). The snapshot
   * is built per recipient, so this costs nothing but the note.
   */
  predictionsIn: z.number().int().min(0),
  predictionsExpected: z.number().int().min(0),
  /** True once this client's own prediction is on record for this turn. */
  selfPredictionSubmitted: z.boolean(),
  /**
   * True once this client has sent `lock_prediction` for this turn.
   *
   * Distinct from `selfPredictionSubmitted` and not implied by it: a complete
   * pair is on record — and scores — from the moment it is complete, whereas
   * locking is the separate, final "do not wait for me". A phone that reloads
   * mid-turn needs both to come back to the right screen: unsubmitted and
   * unlocked returns to picking, submitted-and-unlocked returns to picking with
   * the slots filled and Submit still live, locked returns to the room board
   * with no way back.
   */
  selfPredictionLocked: z.boolean(),
  /**
   * Connected predictors who have not locked yet — the room board's waiting
   * list, restored so a reconnect does not have to wait for the next
   * `prediction_count` to draw it.
   *
   * Empty for the recipient who is the turn player, who is told nothing about
   * the state of anyone's predictions. Never contains anything about the
   * *content* of a prediction, here or anywhere else before the reveal.
   */
  pendingPredictors: z.array(PlayerIdSchema),
  /** Echoed so the client can show progress: "turn 5 of 24". */
  turnsTotal: z.number().int().min(0),
  /** ms epoch of the server clock at send time, for countdown drift correction. */
  serverNow: z.number().int(),
});
export type RoomSnapshot = z.infer<typeof RoomSnapshotSchema>;
